import { Autocomplete, Chip, TextField } from "@mui/material";
import { createFilterOptions } from '@mui/material/Autocomplete';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutocompleteActionsContext, AutocompleteActionsPaper } from './autocomplete_actions_paper';
import { VirtualizedAutocompleteContext, VirtualizedAutocompleteListbox, VirtualizedAutocompleteNavigation, VirtualizedAutocompleteOption } from './virtualized_autocomplete_listbox';

type Props<T> = {
  label?: string;
  value?: T[] | null;
  options: T[];
  required?: boolean;
  helperText?: string | null;
  placeholder?: string;
  readOnly?: boolean;
  chipMaxLength?: number;
  newButtonLabel?: string;
  multiline?: boolean;
  getLimitTagsText?: (more: number) => string;
  getOptionLabel: (opt: T) => any;
  getOptionKey: (opt: T) => any;
  onChange: (newValue: T[] | null) => void;
  onNew?: () => void;
};

export const DefaultMultiAutocomplete = <T,>({
  label, value, options, required, readOnly, helperText, placeholder,
  chipMaxLength, newButtonLabel, multiline, getOptionLabel, getOptionKey,
  getLimitTagsText, onChange, onNew,
}: Props<T>) => {
  const incomingIds = useMemo(() => value?.map(getOptionKey) ?? [], [value, getOptionKey]);
  const previousIncomingIds = useRef(incomingIds);
  const [draftIds, setDraftIds] = useState(incomingIds);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const navigationRef = useRef<VirtualizedAutocompleteNavigation | null>(null);
  const selectedIds = useMemo(() => new Set(draftIds), [draftIds]);
  const selectedOptions = useMemo(() => options.filter(option => selectedIds.has(getOptionKey(option))),
    [options, selectedIds, getOptionKey]);

  useEffect(() => {
    const previous = previousIncomingIds.current;
    previousIncomingIds.current = incomingIds;
    // Inline getters/new array references must not close the popup or discard
    // uncommitted selection. Only an actual external value change resets it.
    if (previous.length === incomingIds.length && previous.every((id, index) => id === incomingIds[index])) return;
    setDraftIds(incomingIds);
    setOpen(false);
    setInputValue('');
  }, [incomingIds]);

  const nativeFilter = useMemo(() => createFilterOptions<T>(), []);
  const filteredOptions = useMemo(() => open ? nativeFilter(options, { inputValue, getOptionLabel }) : [],
    [open, nativeFilter, options, inputValue, getOptionLabel]);
  const filterOptions = useCallback(() => filteredOptions, [filteredOptions]);
  const selectAll = filteredOptions.length > 0 && filteredOptions.every(option => selectedIds.has(getOptionKey(option)));
  const handleSelectAll = useCallback(() => {
    if (readOnly || !filteredOptions.length) return;
    const nextOptions = selectAll ? [] : filteredOptions;
    setDraftIds(nextOptions.map(getOptionKey));
    setOpen(false);
    setInputValue('');
    onChange(nextOptions);
  }, [readOnly, filteredOptions, selectAll, getOptionKey, onChange]);
  const actions = useMemo(() => ({
    onNew, newButtonLabel, disabled: readOnly,
    selectAll: { checked: selectAll, disabled: !filteredOptions.length, onChange: handleSelectAll },
  }), [onNew, newButtonLabel, readOnly, selectAll, filteredOptions.length, handleSelectAll]);
  const contextValue = useMemo(() => ({ navigationRef, inputValue, multiline }), [inputValue, multiline]);

  return (
    <AutocompleteActionsContext.Provider value={actions}>
      <VirtualizedAutocompleteContext.Provider value={contextValue}>
        <Autocomplete
          size="small"
          multiple
          disableCloseOnSelect
          disableListWrap
          readOnly={readOnly}
          getLimitTagsText={more => getLimitTagsText ? getLimitTagsText(more) : `+${more}`}
          limitTags={1}
          getOptionLabel={getOptionLabel}
          getOptionKey={getOptionKey}
          options={options}
          filterOptions={filterOptions}
          value={selectedOptions}
          open={open}
          inputValue={inputValue}
          onInputChange={(_, text) => setInputValue(text)}
          onKeyDown={event => navigationRef.current?.prepareKeyDown(event)}
          onHighlightChange={(_, option, reason) => navigationRef.current?.highlight(
            option === null ? -1 : filteredOptions.indexOf(option), reason)}
          slots={{ paper: AutocompleteActionsPaper }}
          slotProps={{ listbox: { component: VirtualizedAutocompleteListbox } }}
          onOpen={() => setOpen(true)}
          onClose={() => {
            setOpen(false);
            setInputValue('');
            // Keep the existing contract: ordinary selection is committed on close.
            if (!readOnly) onChange(selectedOptions);
          }}
          onChange={(_, nextOptions, reason) => {
            if (readOnly) return;
            setDraftIds(nextOptions.map(getOptionKey));
            if (reason === 'clear') {
              setInputValue('');
              onChange([]);
            }
          }}
          renderValue={(selected, getItemProps) => (
            <>
              {selected.slice(0, 1).map((option, index) => {
                const label = getOptionLabel(option);
                const { key, ...itemProps } = getItemProps({ index });
                return <Chip
                  {...itemProps}
                  key={key}
                  size="small"
                  label={chipMaxLength && label.length > chipMaxLength ? `${label.substring(0, chipMaxLength)}...` : label}
                  onDelete={undefined}
                />;
              })}
              {selected.length > 1 && (getLimitTagsText ? getLimitTagsText(selected.length - 1) : ` +${selected.length - 1}`)}
            </>
          )}
          renderOption={(props, option, { selected }) => ({
            props, label: getOptionLabel(option), selected,
          } satisfies VirtualizedAutocompleteOption) as unknown as ReactNode}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              label={!required ? label : `${label} ${draftIds.length ? '*' : ''}`}
              error={helperText != null}
              helperText={helperText}
              required={required && !draftIds.length}
              placeholder={placeholder}
            />
          )}
        />
      </VirtualizedAutocompleteContext.Provider>
    </AutocompleteActionsContext.Provider>
  );
};
